import { describeReason } from '@fundline/shared/messages/status';
import { formatPhone } from '@fundline/shared/phone';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, PanelRight, UserRound } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Avatar } from '../../components/ui/Avatar.tsx';
import { Badge } from '../../components/ui/Badge.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { EmptyState, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import {
  ComposeWindowNotice,
  countedFromThread,
  heldLabel,
  localTimes,
  useRules,
} from '../compliance/SendingWindow.tsx';
import { previewDecision, useCancelMessage, useNow } from '../compliance/use-sending-rules.ts';
import { useCampaignLabels } from '../campaigns/use-campaigns.ts';
import { useMembersQuery } from '../members/use-members.ts';
import { ComposeBox } from './ComposeBox.tsx';
import { formatDay, formatFull, formatTime, formatWhen, resendAvailableAt } from './format.ts';
import { MessageStatusTicks } from './MessageStatus.tsx';
import {
  type ConversationMessage,
  type MerchantHeader,
  useConversationQuery,
  useInboxMutations,
  useMessageFooterQuery,
} from './use-inbox.ts';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.';
}

/** Why the compose box is off for this merchant, or null. The worker's gate enforces it anyway. */
export function composeDisabledReason(m: MerchantHeader): string | null {
  if (m.archivedAt) return 'the merchant is archived.';
  if (m.optedOutAt)
    return 'the merchant opted out of texts. Only they can resubscribe, by texting START.';
  if (m.doNotContact)
    return `the merchant is marked do not contact${m.dncReason ? ` (${m.dncReason})` : ''}.`;
  if (m.lineType === 'landline') return 'this number is a landline and cannot receive texts.';
  return null;
}

/** The automatic replies a merchant's keyword text gets (M6): labelled instead of a sender. */
const AUTOMATIC: Readonly<Record<string, string>> = {
  opt_out_confirmation: 'Automatic opt-out confirmation',
  resubscribe_confirmation: 'Automatic resubscribe confirmation',
  help_reply: 'Automatic HELP reply',
};

/** A rep typed it (Iris), or an automation sent it (Iris tint), or the merchant did (grey). */
type Voice = 'in' | 'out' | 'auto';

function voiceOf(msg: ConversationMessage): Voice {
  if (msg.direction === 'in') return 'in';
  return msg.kind === 'manual' ? 'out' : 'auto';
}

function statusDetail(msg: ConversationMessage, zones: string[] | null): string | undefined {
  if (msg.status === 'delivered' && msg.deliveredAt) return formatWhen(msg.deliveredAt);
  if (msg.status === 'sent' && msg.sentAt) return formatWhen(msg.sentAt);
  if (msg.status === 'held') return heldLabel(msg, zones).replace(/^Held ?/, '') || undefined;
  return undefined;
}

/** The required replies (M6) can never be cancelled: the opt-out confirmation must go out. */
const NOT_CANCELLABLE = new Set(['opt_out_confirmation', 'help_reply', 'resubscribe_confirmation']);

/** Statuses worth a line under every bubble, not only the last of a group. */
const NOTEWORTHY = new Set(['queued', 'sending', 'held', 'failed', 'unknown', 'blocked']);

function Bubble({
  msg,
  last,
  senderName,
  resent,
  onResend,
  resending,
  zones,
  campaignName,
  onCancel,
  cancelling,
}: {
  msg: ConversationMessage;
  /** M9: the campaign that sent it, or null. */
  campaignName: string | null;
  /** Last bubble of a run from one sender: tail corner and the meta line. */
  last: boolean;
  senderName: string | null;
  resent: boolean;
  onResend: (id: string) => void;
  resending: boolean;
  /** The merchant's candidate zones (M7), for "Held until … their time". */
  zones: string[] | null;
  onCancel: (id: string) => void;
  cancelling: boolean;
}) {
  const voice = voiceOf(msg);
  const out = msg.direction === 'out';
  const reason = describeReason(msg.statusReason);
  const problem = msg.status === 'failed' || msg.status === 'unknown' || msg.status === 'blocked';
  const resendAt = msg.status === 'unknown' ? resendAvailableAt(msg.updatedAt) : null;
  const canResend =
    !resent &&
    !msg.pending &&
    (msg.status === 'failed' ||
      (msg.status === 'unknown' && resendAt !== null && resendAt <= new Date()));
  const showMeta = last || (out && NOTEWORTHY.has(msg.status));
  const side = out ? 'fl-meta--out' : 'fl-meta--in';
  return (
    <li
      className={`flex flex-col gap-1 ${out ? 'items-end' : 'items-start'}`}
      data-testid="message"
      data-direction={msg.direction}
    >
      <div
        className={`fl-bubble fl-bubble--${voice} whitespace-pre-wrap ${last ? 'fl-bubble--last' : ''} ${
          problem ? 'fl-bubble--failed' : ''
        }`}
      >
        {msg.body}
      </div>
      {showMeta ? (
        <div className={`fl-meta ${side} flex flex-wrap items-center gap-x-1`}>
          <span title={formatFull(msg.createdAt)}>{formatTime(msg.createdAt)}</span>
          {out && AUTOMATIC[msg.kind] ? (
            <span className="text-brand-text" data-testid="automatic-reply">
              · {AUTOMATIC[msg.kind]}
            </span>
          ) : out && campaignName ? (
            <span className="text-brand-text" data-testid="campaign-label">
              · {msg.campaignTest ? 'Test of' : 'Campaign:'} {campaignName}
            </span>
          ) : out && senderName ? (
            <span>· {senderName}</span>
          ) : null}
          {out ? (
            <MessageStatusTicks status={msg.status} detail={statusDetail(msg, zones)} />
          ) : null}
        </div>
      ) : null}
      {out && msg.status === 'cancelled' && msg.campaignId && reason ? (
        <p className={`fl-meta ${side} max-w-[75%] text-ink-muted`} data-testid="message-reason">
          {reason}
        </p>
      ) : null}
      {out && (problem || msg.status === 'held') && reason ? (
        <p
          className={`fl-meta ${side} max-w-[75%] ${problem ? 'fl-meta--danger' : 'text-warning'}`}
          data-testid="message-reason"
        >
          {reason}
        </p>
      ) : null}
      {out && msg.status === 'unknown' && !canResend && !resent && resendAt ? (
        <p className={`fl-meta ${side} max-w-[75%] text-warning`}>
          Waiting for the carrier&apos;s report. If none arrives, you can resend at{' '}
          {resendAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}.
        </p>
      ) : null}
      {canResend ? (
        <div className="self-end">
          <Button variant="secondary" size="sm" busy={resending} onClick={() => onResend(msg.id)}>
            Resend
          </Button>
        </div>
      ) : null}
      {out && msg.status === 'held' && !msg.pending && !NOT_CANCELLABLE.has(msg.kind) ? (
        <div className="self-end">
          <Button
            variant="secondary"
            size="sm"
            busy={cancelling}
            onClick={() => onCancel(msg.id)}
            aria-label="Cancel this held text"
          >
            Cancel text
          </Button>
        </div>
      ) : null}
      {resent && out ? <p className={`fl-meta ${side}`}>Resent below.</p> : null}
    </li>
  );
}

const HEADER_ICON =
  'fl-icon-btn text-ink-muted hover:bg-surface hover:text-ink focus-visible:outline-2 focus-visible:outline-focus-ring';

/** One conversation: header, thread (day separators, bubble groups, status ticks), composer. */
export function ConversationView({
  merchantId,
  detailsOpen,
  onToggleDetails,
}: {
  merchantId: string;
  detailsOpen: boolean;
  onToggleDetails: () => void;
}) {
  const { workspaceId } = useWorkspaceClaims();
  const conversation = useConversationQuery(workspaceId, merchantId);
  // M9: campaign texts say which campaign sent them (reps cannot read campaigns; names only).
  const campaignNames = useCampaignLabels(
    workspaceId,
    (conversation.data?.messages ?? []).flatMap((m) => (m.campaignId ? [m.campaignId] : [])),
  );
  const footer = useMessageFooterQuery(workspaceId, merchantId);
  const members = useMembersQuery(workspaceId);
  const { send, resend, markRead } = useInboxMutations(workspaceId, merchantId);
  const cancel = useCancelMessage(workspaceId);
  const rules = useRules(workspaceId);
  const now = useNow();
  const [sendError, setSendError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  const messages = conversation.data?.messages;
  const unread = messages?.some((m) => m.direction === 'in' && !m.isRead) ?? false;
  const lastId = messages?.at(-1)?.id;

  // Opening a conversation (or a new text arriving while it is open) marks it read.
  const { mutate: markReadNow, isPending: marking } = markRead;
  useEffect(() => {
    if (unread && !marking) markReadNow();
  }, [unread, marking, markReadNow]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [lastId]);

  const back = (
    <Link
      to="/inbox"
      search={(prev) => prev}
      className={`${HEADER_ICON} split:hidden`}
      aria-label="All conversations"
    >
      <ChevronLeft size={24} strokeWidth={1.75} aria-hidden />
    </Link>
  );

  if (conversation.isPending) {
    return (
      <div className="p-4">
        {back}
        <Spinner label="Loading the conversation" />
      </div>
    );
  }
  if (conversation.error) {
    return (
      <div className="p-4">
        {back}
        <Alert tone="error">{conversation.error.message}</Alert>
      </div>
    );
  }
  const merchant = conversation.data.merchant;
  if (!merchant) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {back}
        <EmptyState title="Merchant not found">
          It doesn&apos;t exist, or it&apos;s assigned to someone else.
        </EmptyState>
      </div>
    );
  }
  const names = new Map((members.data ?? []).map((m) => [m.userId, m.fullName || m.email]));
  const title = merchant.businessName ?? merchant.ownerName ?? formatPhone(merchant.phone);
  const thread = conversation.data.messages;
  // M7 preview of the worker's decision for one more reply (the final gate has the last word).
  const decision =
    rules.data && rules.overrides
      ? previewDecision(
          merchant,
          'reply',
          rules.data,
          rules.overrides,
          countedFromThread(thread, now),
          now,
        )
      : null;
  const zones = decision
    ? decision.ok
      ? decision.location.zones
      : decision.action === 'hold'
        ? (decision.location?.zones ?? null)
        : null
    : null;
  const disabled =
    composeDisabledReason(merchant) ??
    (decision && !decision.ok && decision.action === 'block'
      ? "this number is outside the US. Fundline can't text it."
      : null);
  const firstName = merchant.ownerName?.trim().split(/\s+/)[0] ?? merchant.businessName ?? null;

  return (
    <section
      className="flex min-h-dvh flex-1 flex-col md:min-h-0"
      aria-label={`Conversation with ${title}`}
    >
      <header className="sticky top-0 z-10 flex min-h-18 items-center gap-2 border-b border-hairline bg-bg px-2 py-2 md:static md:px-4">
        {back}
        <Avatar name={merchant.businessName ?? merchant.ownerName} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <h2 className="headline truncate text-ink" data-testid="conversation-title">
              {title}
            </h2>
            {merchant.optedOutAt ? <Badge role="declined">Opted out</Badge> : null}
            {merchant.doNotContact ? <Badge role="declined">Do not contact</Badge> : null}
            {merchant.lineType === 'landline' ? <Badge role="declined">Landline</Badge> : null}
            {merchant.archivedAt ? <Badge role="neutral">Archived</Badge> : null}
          </div>
          <p className="footnote truncate text-ink-muted">
            {merchant.businessName && merchant.ownerName ? `${merchant.ownerName} · ` : ''}
            <span className="fl-num">{formatPhone(merchant.phone)}</span>
            {merchant.state ? ` · ${merchant.state}` : ''}
            {zones ? (
              <span data-testid="header-local-time">{` · ${localTimes(now, zones)}`}</span>
            ) : null}
            {` · ${names.get(merchant.assignedRepId) ?? 'Assigned rep'}`}
          </p>
        </div>
        <Link
          to="/merchants/$merchantId"
          params={{ merchantId }}
          className={HEADER_ICON}
          aria-label="Profile"
          title="Open the merchant's profile"
        >
          <UserRound size={20} strokeWidth={1.75} aria-hidden />
        </Link>
        <button
          type="button"
          className={`${HEADER_ICON} ${detailsOpen ? 'bg-brand-tint text-brand-on-tint' : ''}`}
          aria-label={detailsOpen ? 'Hide details' : 'Show details'}
          aria-expanded={detailsOpen}
          title="Details"
          onClick={onToggleDetails}
        >
          <PanelRight size={20} strokeWidth={1.75} aria-hidden />
        </button>
      </header>

      <div className="flex-1 md:overflow-y-auto">
        {conversation.data.truncated ? (
          <p className="footnote pt-3 text-center text-ink-muted">Showing the latest 200 texts.</p>
        ) : null}
        {thread.length === 0 ? (
          <div className="p-4">
            <EmptyState title="No texts yet">Write the first one below.</EmptyState>
          </div>
        ) : (
          <ol className="fl-thread" aria-label="Texts" data-testid="thread">
            {thread.map((msg, i) => {
              const prev = thread[i - 1];
              const next = thread[i + 1];
              const day = formatDay(msg.createdAt);
              const newDay = !prev || formatDay(prev.createdAt) !== day;
              const newRun = !prev || newDay || voiceOf(prev) !== voiceOf(msg);
              const last =
                !next || voiceOf(next) !== voiceOf(msg) || formatDay(next.createdAt) !== day;
              return (
                <Fragment key={msg.id}>
                  {newDay ? (
                    <li
                      className="footnote self-center py-2 font-semibold text-ink-muted"
                      aria-hidden
                    >
                      {day}
                    </li>
                  ) : newRun && i > 0 ? (
                    <li className="fl-group-break" aria-hidden />
                  ) : null}
                  <Bubble
                    msg={msg}
                    last={last}
                    senderName={msg.senderUserId ? (names.get(msg.senderUserId) ?? null) : null}
                    resent={conversation.data.resentIds.has(msg.id)}
                    resending={resend.isPending && resend.variables === msg.id}
                    onResend={(id) => {
                      resend.mutate(id, { onError: (err) => setSendError(messageOf(err)) });
                    }}
                    zones={zones}
                    campaignName={
                      msg.campaignId
                        ? (campaignNames.data?.get(msg.campaignId) ?? 'a campaign')
                        : null
                    }
                    cancelling={cancel.isPending && cancel.variables === msg.id}
                    onCancel={(id) => {
                      cancel.mutate(id, { onError: (err) => setSendError(messageOf(err)) });
                    }}
                  />
                </Fragment>
              );
            })}
          </ol>
        )}
        <div ref={bottom} />
      </div>

      <div className="sticky bottom-0 border-t border-hairline bg-bg pb-[env(safe-area-inset-bottom)] md:static">
        <ComposeBox
          recipientName={firstName}
          footer={footer.data ?? null}
          disabledReason={disabled}
          notice={
            decision && !disabled ? (
              <ComposeWindowNotice decision={decision} name={firstName ?? title} now={now} />
            ) : null
          }
          sending={send.isPending}
          error={sendError}
          onSend={async (input) => {
            setSendError(null);
            try {
              await send.mutateAsync(input);
              return true;
            } catch (err) {
              setSendError(messageOf(err));
              return false;
            }
          }}
        />
      </div>
    </section>
  );
}
